import type { TourStep } from '@/components/ui/Tour';
import type { StartTourWithCompletionKeyOptions } from '@/components/tour/PageTourHost';
import type { PageTourKey } from './registry';
import {
  clubCourtHireBookingsTourSteps,
  clubCourtHireRatesTourSteps,
  clubCourtHireSettingsTourSteps,
  type ClubCourtHireTourOptions,
  getClubCourtHireTourSteps,
} from './club-court-hire-tour-steps';
import { schedulePageTourStart, scheduleTourStepsStart } from './scheduleAutoPageTour';

export type { ClubCourtHireTourOptions };

export type ClubCourtHireTourTab = 'bookings' | 'rates' | 'settings';

export const CLUB_COURT_HIRE_TOUR_KEY: PageTourKey = 'club-court-hire';

export const CLUB_COURT_HIRE_TAB_TOUR_KEYS: Record<ClubCourtHireTourTab, PageTourKey> = {
  bookings: 'club-court-hire-bookings',
  rates: 'club-court-hire-rates',
  settings: 'club-court-hire-settings',
};

export function resolveClubCourtHireTabTourKey(tab: ClubCourtHireTourTab): PageTourKey {
  return CLUB_COURT_HIRE_TAB_TOUR_KEYS[tab];
}

export interface ClubCourtHireTabHelpContent {
  title: string;
  description: string;
  tourLabel?: string;
  tourDescription?: string;
}

export const CLUB_COURT_HIRE_PAGE_HELP = {
  title: 'Court hire',
  description:
    'Manage guest and member court rentals, set hire rates, and configure public court hire settings.',
  tourLabel: 'Tour this page',
  tourDescription: 'Walk through bookings, rates, settings, and creating a hire rate.',
};

export const CLUB_COURT_HIRE_TAB_HELP: Record<ClubCourtHireTourTab, ClubCourtHireTabHelpContent> = {
  bookings: {
    title: 'Court hire bookings',
    description: 'Review guest and member court rentals and filter by status.',
  },
  rates: {
    title: 'Hire rates',
    description: 'Create session lengths and prices for public court hire.',
  },
  settings: {
    title: 'Court hire settings',
    description: 'Enable guest checkout and configure public booking rules.',
  },
};

export function getClubCourtHireTabHelp(tab: ClubCourtHireTourTab): ClubCourtHireTabHelpContent {
  return CLUB_COURT_HIRE_TAB_HELP[tab];
}

export function getClubCourtHireTabTourSteps(tab: ClubCourtHireTourTab): TourStep[] {
  switch (tab) {
    case 'bookings':
      return clubCourtHireBookingsTourSteps;
    case 'rates':
      return clubCourtHireRatesTourSteps;
    case 'settings':
      return clubCourtHireSettingsTourSteps;
    default:
      return clubCourtHireBookingsTourSteps;
  }
}

export function scheduleClubCourtHireTourStart(
  startTourWithCompletionKey: (
    completionKey: string,
    steps: TourStep[],
    options?: StartTourWithCompletionKeyOptions,
  ) => void,
  options: ClubCourtHireTourOptions = {},
): () => void {
  const steps = getClubCourtHireTourSteps(options);
  return scheduleTourStepsStart(
    () =>
      startTourWithCompletionKey(CLUB_COURT_HIRE_TOUR_KEY, steps, {
        onFinish: options.onFinish,
      }),
    steps,
  );
}

export function scheduleClubCourtHireTabTourStart(
  startTourWithCompletionKey: (completionKey: string, steps: TourStep[]) => void,
  tab: ClubCourtHireTourTab,
  onEnterTab?: () => void,
): () => void {
  const tourKey = resolveClubCourtHireTabTourKey(tab);
  const steps = getClubCourtHireTabTourSteps(tab).map((step, index) => {
    if (index === 0 && onEnterTab) {
      return { ...step, onStepEnter: onEnterTab };
    }
    return step;
  });
  return schedulePageTourStart(
    () => startTourWithCompletionKey(tourKey, steps),
    tourKey,
  );
}
